import React, { CSSProperties, useState } from 'react';
import {
  LEAVE_WAITLIST_TITLE,
  REMOVE_WAITLIST_BUTTON,
  REMOVE_WAITLIST_MESSAGE,
  STAY_WAITLIST_BUTTON
} from 'constants/waitlistStrings';
import { useAppDispatch } from 'hooks';
import { removeWaitlistEntry } from 'reducers/waitlist';
import { leaveWaitlist } from 'services/waitlistService';
import type { WaitlistGameType } from 'types/waitlist';
import { markRecentlyLeftWaitlist } from 'utils/recentlyLeftWaitlists';

import GameModal, {
  btnRowStyle,
  primaryBtnStyle,
  secondaryBtnStyle
} from 'components/GameModal/GameModal';

/**
 * Mirrors Unity's leave-waitlist confirmation from
 * TablesView.cs:1708/:2623 and MenuForTablesView.cs:430.
 * Strings are sourced from constants/waitlistStrings.ts which is pinned
 * to Unity's GameConstants.cs verbatim.
 *
 * Built on the shared branded GameModal (dark navy + gold) so it matches the
 * Buy-In modal and the rest of the system modals (WL-002).
 */
type LeaveWaitlistConfirmModalProps = {
  open: boolean;
  gameId: number;
  gameType: WaitlistGameType;
  playerGuid: string;
  onClose: () => void;
  onLeft?: () => void;
  onError?: (err: Error) => void;
};

const LeaveWaitlistConfirmModal: React.FC<LeaveWaitlistConfirmModalProps> = ({
  open,
  gameId,
  gameType,
  playerGuid,
  onClose,
  onLeft,
  onError
}) => {
  const dispatch = useAppDispatch();
  const [pending, setPending] = useState(false);

  const handleConfirm = async () => {
    if (pending) return;
    setPending(true);
    try {
      await leaveWaitlist({ gameId, gameType, playerGuid });
      dispatch(removeWaitlistEntry({ gameId, playerGuid, myGuid: playerGuid }));
      // Guard against a lagging engine broadcast re-adding this spot before
      // the DELETE is processed (which would re-hide the lobby banner).
      markRecentlyLeftWaitlist(gameId);
      onLeft?.();
      onClose();
    } catch (err) {
      onError?.(err as Error);
    } finally {
      setPending(false);
    }
  };

  return (
    <GameModal open={open} onClose={pending ? undefined : onClose} title={LEAVE_WAITLIST_TITLE}>
      <div style={messageStyle}>{REMOVE_WAITLIST_MESSAGE}</div>
      <div style={btnRowStyle}>
        <button
          onClick={handleConfirm}
          disabled={pending}
          style={{ ...primaryBtnStyle, opacity: pending ? 0.5 : 1 }}
          aria-label='confirm-leave-waitlist'>
          {REMOVE_WAITLIST_BUTTON}
        </button>
        <button
          onClick={onClose}
          disabled={pending}
          style={secondaryBtnStyle}
          aria-label='stay-on-waitlist'>
          {STAY_WAITLIST_BUTTON}
        </button>
      </div>
    </GameModal>
  );
};

export default LeaveWaitlistConfirmModal;

const messageStyle: CSSProperties = {
  fontSize: '0.9rem',
  color: 'rgba(255, 255, 255, 0.7)',
  lineHeight: 1.5,
  textAlign: 'center',
  marginBottom: '0.5rem'
};
